import * as React from 'react'
import { ReactElement, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { COMMAND_PREFIX } from './Commands'
import { useHistory } from 'react-router-dom'
import { taskBaseUrl } from './Tasks'
import { MenuItemKey } from '../components/MainMenu'
import { AppAction } from '../config/appConfig'
import { FileAction } from '../config/fileConfig'
import useUser from '../hooks/useUser'
import { ModuleType } from '../context/GlobalContext'
import useModules from '../hooks/module/useModules'
import useRoles, { rolesTasks, rolesTimes } from '../hooks/useRoles'
import { useSettings } from '../hooks/useSettings'
import { useTranslation } from 'react-i18next'
import useTaskStatuses from '../hooks/useTaskStatuses'
import {
    bundleIcon,
    ClipboardTaskAddFilled,
    ClipboardTaskAddRegular,
    TabDesktopSearchFilled,
    TabDesktopSearchRegular,
    TimerFilled,
    TimerRegular,
} from '@fluentui/react-icons'
import { makeStyles, shorthands, Text, tokens, Toolbar, ToolbarButton } from '@fluentui/react-components'
import BaseTaskOverview, { TaskFilterType } from './Task/BaseTaskOverview'
import FileOverview from './File/FileOverview'

const AddTime = bundleIcon(TimerFilled, TimerRegular)
const SearchFile = bundleIcon(TabDesktopSearchFilled, TabDesktopSearchRegular)
const AddTask = bundleIcon(ClipboardTaskAddFilled, ClipboardTaskAddRegular)

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.margin(tokens.spacingHorizontalL),
    },
    toolbar: {
        alignSelf: 'center',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingHorizontalL),
    },
})

export default function Dashboard(props): ReactElement {
    const dispatch = useDispatch()
    const history = useHistory()
    const modules = useModules()
    const roles = useRoles()
    const settings = useSettings()
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const { currentUser } = useUser()
    const hasTaskAuthorization = useMemo<boolean>(() => roles.any(rolesTasks), [roles.loaded])
    const hasTimeAuthorization = useMemo<boolean>(() => roles.any(rolesTimes), [roles.loaded])
    const showRecentFiles = useMemo(() => settings?.dashboard?.showRecentFiles !== false, [settings])
    const { loaded: tasksLoaded, getStartStatus } = useTaskStatuses()

    const assigneeFilterValues = useMemo<Partial<TaskFilterType>>(
        () => ({
            assigners: [],
            assignees: [currentUser.id],
            statuses: [getStartStatus().alpha],
            from: undefined,
            to: undefined,
        }),
        [tasksLoaded],
    )

    const assignerFilterValues = useMemo<Partial<TaskFilterType>>(
        () => ({
            assigners: [currentUser.id],
            assignees: [],
            notAssignees: [currentUser.id],
            statuses: [getStartStatus().alpha],
            from: undefined,
            to: undefined,
        }),
        [tasksLoaded],
    )

    useEffect(() => {
        dispatch({ type: AppAction.SetBreadcrumbs, payload: [MenuItemKey.Dashboard] })
        dispatch({ type: FileAction.ResetCurrent })
    }, [props])

    return (
        <>
            <div className={styles.container}>
                <Toolbar className={styles.toolbar}>
                    {modules.has(ModuleType.TimeTracking) && hasTimeAuthorization && (
                        <ToolbarButton
                            vertical
                            icon={<AddTime />}
                            onClick={() => history.push(`${COMMAND_PREFIX}/add-time`)}
                        >
                            {translate('action.create.time.title')}
                        </ToolbarButton>
                    )}
                    <ToolbarButton
                        vertical
                        icon={<SearchFile />}
                        onClick={() => history.push(`${COMMAND_PREFIX}/search-file`)}
                    >
                        {translate('action.search.file.title')}
                    </ToolbarButton>
                    {modules.has(ModuleType.Tasks) && hasTaskAuthorization && (
                        <ToolbarButton
                            vertical
                            icon={<AddTask />}
                            onClick={() => history.push(`${COMMAND_PREFIX}/add-task`)}
                        >
                            {translate('action.create.task.title')}
                        </ToolbarButton>
                    )}
                </Toolbar>

                <div className={styles.tableContainer}>
                    {modules.has(ModuleType.Tasks) && hasTaskAuthorization && (
                        <>
                            <Text size={600}>{translate('dashboard.task.assignee.title')}</Text>
                            <BaseTaskOverview
                                baseUrl={taskBaseUrl}
                                batchSizeItems={20}
                                maxCount={20}
                                fixedFilterValues={assigneeFilterValues}
                                filterContent={undefined}
                                localStorageFilterKey={undefined}
                                localStorageSortingKey={undefined}
                            />
                            <Text size={600}>{translate('dashboard.task.assigner.title')}</Text>
                            <BaseTaskOverview
                                baseUrl={taskBaseUrl}
                                batchSizeItems={20}
                                maxCount={20}
                                fixedFilterValues={assignerFilterValues}
                                filterContent={undefined}
                                localStorageFilterKey={undefined}
                                localStorageSortingKey={undefined}
                            />
                        </>
                    )}
                    {showRecentFiles && (
                        <>
                            <Text size={600}>{translate('dashboard.file.title')}</Text>
                            <FileOverview
                                batchSizeItems={20}
                                maxCount={20}
                                filterContent={undefined}
                                localStorageFilterKey={undefined}
                                localStorageSortingKey={undefined}
                            />
                        </>
                    )}
                </div>
            </div>
        </>
    )
}
